<!--
  ThemedTooltip Component
  
  A reusable, accessible tooltip component for Theme v1 that supports flexible positioning,
  keyboard navigation, and automatic theme adaptation.
  
  Usage Examples:
  
  Basic text tooltip:
  <themed-tooltip text="This is helpful information">
    <button>Hover me</button>
  </themed-tooltip>
  
  Rich content tooltip:
  <themed-tooltip placement="right">
    <template #default>
      <span class="info-icon">ℹ️</span>
    </template>
    <template #content>
      <strong>Important:</strong> This action cannot be undone.
    </template>
  </themed-tooltip>
  
  Custom configuration:
  <themed-tooltip 
    text="Delayed tooltip"
    placement="bottom"
    :show-delay="500"
    :offset="12"
    max-width="400px"
  >
    <button>Custom tooltip</button>
  </themed-tooltip>
  
  Props:
  - text (String): Content to display in tooltip (alternative to content slot)
  - placement (String): Preferred placement ('top', 'bottom', 'left', 'right', 'top-start', 'top-end', 'bottom-start', 'bottom-end')
  - showDelay (Number): Delay in ms before showing tooltip on hover (default: 200)
  - hideDelay (Number): Delay in ms before hiding tooltip (default: 0)
  - disabled (Boolean): Whether tooltip is disabled (default: false)
  - offset (Number): Distance from trigger element in pixels (default: 8)
  - maxWidth (String): Maximum width of tooltip (default: '300px')
  - showArrow (Boolean): Whether to show arrow pointer (default: true)
  
  Slots:
  - default: The trigger element that activates the tooltip
  - content: Rich HTML content for the tooltip (alternative to text prop)
  
  Events:
  - show: Emitted when tooltip becomes visible
  - hide: Emitted when tooltip is hidden
  
  Methods (accessible via ref):
  - show(): Programmatically show the tooltip
  - hide(): Programmatically hide the tooltip
  - updatePosition(): Recalculate and update tooltip position
-->
<template>
  <div class="themed-tooltip-wrapper" ref="wrapper">
    <!-- Trigger element slot -->
    <div
      ref="trigger"
      class="themed-tooltip-trigger"
      @mouseenter="handleMouseEnter"
      @mouseleave="handleMouseLeave"
      @focus="handleFocus"
      @blur="handleBlur"
      :aria-describedby="isVisible ? tooltipId : null"
    >
      <slot></slot>
    </div>

    <!-- Tooltip element (conditionally rendered) -->
    <transition name="tooltip-fade">
      <div
        v-if="isVisible"
        :id="tooltipId"
        ref="tooltip"
        role="tooltip"
        class="themed-tooltip"
        :class="[`themed-tooltip--${actualPlacement}`, { 'themed-tooltip--no-arrow': !showArrow }]"
        :style="tooltipStyle"
      >
        <!-- Tooltip content -->
        <div class="themed-tooltip__content">
          <slot name="content">{{ text }}</slot>
        </div>

        <!-- Arrow pointer -->
        <div v-if="showArrow" class="themed-tooltip__arrow" :style="arrowStyle"></div>
      </div>
    </transition>
  </div>
</template>

<script>
let tooltipIdCounter = 0;

export default {
  name: 'ThemedTooltip',

  props: {
    /**
     * Content to display in tooltip (alternative to content slot)
     * @type {String}
     * @default ''
     */
    text: {
      type: String,
      default: ''
    },

    /**
     * Preferred placement of tooltip relative to trigger
     * @type {String}
     * @default 'top'
     * @values 'top', 'bottom', 'left', 'right', 'top-start', 'top-end', 'bottom-start', 'bottom-end'
     */
    placement: {
      type: String,
      default: 'top',
      validator: (value) => ['top', 'bottom', 'left', 'right', 'top-start', 'top-end', 'bottom-start', 'bottom-end'].includes(value)
    },

    /**
     * Delay in milliseconds before showing tooltip on hover
     * @type {Number}
     * @default 200
     */
    showDelay: {
      type: Number,
      default: 200
    },

    /**
     * Delay in milliseconds before hiding tooltip
     * @type {Number}
     * @default 0
     */
    hideDelay: {
      type: Number,
      default: 0
    },

    /**
     * Whether tooltip is disabled
     * @type {Boolean}
     * @default false
     */
    disabled: {
      type: Boolean,
      default: false
    },

    /**
     * Custom offset from trigger element in pixels
     * @type {Number}
     * @default 8
     */
    offset: {
      type: Number,
      default: 8
    },

    /**
     * Maximum width of tooltip
     * @type {String}
     * @default '300px'
     */
    maxWidth: {
      type: String,
      default: '300px'
    },

    /**
     * Whether to show arrow pointer
     * @type {Boolean}
     * @default true
     */
    showArrow: {
      type: Boolean,
      default: true
    }
  },

  data() {
    return {
      isVisible: false,
      position: {
        top: 0,
        left: 0
      },
      actualPlacement: this.placement,
      tooltipId: `tooltip-${++tooltipIdCounter}`,
      showTimeout: null,
      hideTimeout: null,
      arrowOffset: 0,
      updatePositionDebounced: null,
      rafId: null
    };
  },

  computed: {
    tooltipStyle() {
      return {
        position: 'fixed',
        top: `${this.position.top}px`,
        left: `${this.position.left}px`,
        maxWidth: this.maxWidth,
        zIndex: 2147483647
      };
    },

    arrowStyle() {
      if (this.actualPlacement.includes('start') || this.actualPlacement.includes('end')) {
        return {
          [this.actualPlacement.includes('top') || this.actualPlacement.includes('bottom') ? 'left' : 'top']: `${this.arrowOffset}px`
        };
      }
      return {};
    }
  },

  methods: {
    /**
     * Handle mouse enter event on trigger element
     */
    handleMouseEnter() {
      if (this.disabled) return;

      // Clear any pending hide timeout
      if (this.hideTimeout) {
        clearTimeout(this.hideTimeout);
        this.hideTimeout = null;
      }

      // Set show timeout
      this.showTimeout = setTimeout(() => {
        this.show();
      }, this.showDelay);
    },

    /**
     * Handle mouse leave event on trigger element
     */
    handleMouseLeave() {
      if (this.disabled) return;

      // Clear any pending show timeout
      if (this.showTimeout) {
        clearTimeout(this.showTimeout);
        this.showTimeout = null;
      }

      // Set hide timeout
      if (this.hideDelay > 0) {
        this.hideTimeout = setTimeout(() => {
          this.hide();
        }, this.hideDelay);
      } else {
        this.hide();
      }
    },

    /**
     * Handle focus event on trigger element
     */
    handleFocus() {
      if (this.disabled) return;
      this.show();
    },

    /**
     * Handle blur event on trigger element
     */
    handleBlur() {
      if (this.disabled) return;
      this.hide();
    },

    /**
     * Programmatically show the tooltip
     */
    show() {
      if (this.disabled || this.isVisible) return;

      this.isVisible = true;
      this.$emit('show');

      // Add ESC key listener
      document.addEventListener('keydown', this.handleEscKey);

      // Calculate position on next tick after tooltip is rendered
      this.$nextTick(() => {
        this.updatePosition();
      });
    },

    /**
     * Programmatically hide the tooltip
     */
    hide() {
      if (!this.isVisible) return;

      this.isVisible = false;
      this.$emit('hide');

      // Remove ESC key listener
      document.removeEventListener('keydown', this.handleEscKey);
    },

    /**
     * Recalculate and update tooltip position
     */
    updatePosition() {
      if (!this.$refs.trigger || !this.$refs.tooltip) {
        if (import.meta.env.DEV) {
          console.warn('ThemedTooltip: Cannot calculate position - refs not available');
        }
        return;
      }

      const triggerRect = this.$refs.trigger.getBoundingClientRect();
      const tooltipRect = this.$refs.tooltip.getBoundingClientRect();
      
      // Calculate position based on placement
      let calculated = this.calculatePosition(triggerRect, tooltipRect, this.placement);
      
      // Check viewport constraints and adjust if needed
      calculated = this.adjustForViewport(calculated, tooltipRect, triggerRect);
      
      this.position = {
        top: calculated.top,
        left: calculated.left
      };
      this.actualPlacement = calculated.placement;
      this.arrowOffset = calculated.arrowOffset;
    },

    /**
     * Calculate tooltip position based on placement and trigger position
     */
    calculatePosition(triggerRect, tooltipRect, placement) {
      // For fixed positioning, we don't need scroll offsets
      // triggerRect already gives us viewport-relative coordinates
      
      let top = 0;
      let left = 0;
      let arrowOffset = 0;
      
      // Validate placement, fallback to 'top' if invalid
      const validPlacements = ['top', 'bottom', 'left', 'right', 'top-start', 'top-end', 'bottom-start', 'bottom-end'];
      if (!validPlacements.includes(placement)) {
        if (import.meta.env.DEV) {
          console.warn(`ThemedTooltip: Invalid placement "${placement}", falling back to "top"`);
        }
        placement = 'top';
      }
      
      // Calculate base position for each placement (using fixed positioning)
      switch (placement) {
        case 'top':
        case 'top-start':
        case 'top-end':
          top = triggerRect.top - tooltipRect.height - this.offset;
          if (placement === 'top-start') {
            left = triggerRect.left;
            arrowOffset = 12; // Arrow offset from left edge
          } else if (placement === 'top-end') {
            left = triggerRect.right - tooltipRect.width;
            arrowOffset = tooltipRect.width - 12; // Arrow offset from left edge
          } else {
            left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
          }
          break;
          
        case 'bottom':
        case 'bottom-start':
        case 'bottom-end':
          top = triggerRect.bottom + this.offset;
          if (placement === 'bottom-start') {
            left = triggerRect.left;
            arrowOffset = 12;
          } else if (placement === 'bottom-end') {
            left = triggerRect.right - tooltipRect.width;
            arrowOffset = tooltipRect.width - 12;
          } else {
            left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
          }
          break;
          
        case 'left':
          top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
          left = triggerRect.left - tooltipRect.width - this.offset;
          break;
          
        case 'right':
          top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
          left = triggerRect.right + this.offset;
          break;
          
        default:
          // Fallback to top
          top = triggerRect.top - tooltipRect.height - this.offset;
          left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
      }
      
      return {
        top,
        left,
        placement,
        arrowOffset
      };
    },

    /**
     * Adjust tooltip position to stay within viewport
     */
    adjustForViewport(calculated, tooltipRect, triggerRect) {
      const viewportWidth = window.innerWidth;
      const viewportHeight = window.innerHeight;
      
      let { top, left, placement, arrowOffset } = calculated;
      
      // Check if tooltip overflows viewport (for fixed positioning)
      const overflowTop = top < 0;
      const overflowBottom = (top + tooltipRect.height) > viewportHeight;
      const overflowLeft = left < 0;
      const overflowRight = (left + tooltipRect.width) > viewportWidth;
      
      // Try alternative placements if current placement causes overflow
      if (placement.startsWith('top') && overflowTop) {
        // Try bottom placement
        const altPlacement = placement.replace('top', 'bottom');
        const altCalculated = this.calculatePosition(triggerRect, tooltipRect, altPlacement);
        if ((altCalculated.top - scrollY + tooltipRect.height) <= viewportHeight) {
          return altCalculated;
        }
      } else if (placement.startsWith('bottom') && overflowBottom) {
        // Try top placement
        const altPlacement = placement.replace('bottom', 'top');
        const altCalculated = this.calculatePosition(triggerRect, tooltipRect, altPlacement);
        if ((altCalculated.top - scrollY) >= 0) {
          return altCalculated;
        }
      } else if (placement === 'left' && overflowLeft) {
        // Try right placement
        const altCalculated = this.calculatePosition(triggerRect, tooltipRect, 'right');
        if ((altCalculated.left - scrollX + tooltipRect.width) <= viewportWidth) {
          return altCalculated;
        }
      } else if (placement === 'right' && overflowRight) {
        // Try left placement
        const altCalculated = this.calculatePosition(triggerRect, tooltipRect, 'left');
        if ((altCalculated.left - scrollX) >= 0) {
          return altCalculated;
        }
      }
      
      // Adjust horizontal position if overflowing
      if (overflowLeft) {
        left = 8; // 8px padding from edge
      } else if (overflowRight) {
        left = viewportWidth - tooltipRect.width - 8;
      }
      
      // Adjust vertical position if overflowing
      if (overflowTop) {
        top = 8;
      } else if (overflowBottom) {
        top = viewportHeight - tooltipRect.height - 8;
      }
      
      return {
        top,
        left,
        placement,
        arrowOffset
      };
    },

    /**
     * Handle ESC key press to dismiss tooltip
     */
    handleEscKey(event) {
      if (event.key === 'Escape' || event.keyCode === 27) {
        this.hide();
      }
    },

    /**
     * Handle scroll and resize events with debouncing and RAF
     */
    handleScrollResize() {
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
      }
      
      this.rafId = requestAnimationFrame(() => {
        if (this.isVisible) {
          this.updatePosition();
        }
        this.rafId = null;
      });
    },

    /**
     * Debounce function for scroll/resize events
     */
    debounce(func, wait) {
      let timeout;
      return function executedFunction(...args) {
        const later = () => {
          clearTimeout(timeout);
          func(...args);
        };
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
      };
    },

    /**
     * Clean up timeouts
     */
    clearTimeouts() {
      if (this.showTimeout) {
        clearTimeout(this.showTimeout);
        this.showTimeout = null;
      }
      if (this.hideTimeout) {
        clearTimeout(this.hideTimeout);
        this.hideTimeout = null;
      }
      if (this.rafId) {
        cancelAnimationFrame(this.rafId);
        this.rafId = null;
      }
    },
    
    /**
     * Handle content update event
     */
    handleContentUpdate() {
      if (this.isVisible) {
        this.$nextTick(() => {
          this.updatePosition();
        });
      }
    }
  },

  mounted() {
    // Check if trigger element exists
    if (!this.$slots.default || this.$slots.default.length === 0) {
      if (import.meta.env.DEV) {
        console.warn('ThemedTooltip: No trigger element provided in default slot');
      }
      return;
    }
    
    // Create debounced version of scroll/resize handler
    this.updatePositionDebounced = this.debounce(this.handleScrollResize, 100);
    
    // Add scroll and resize listeners
    window.addEventListener('scroll', this.updatePositionDebounced, true);
    window.addEventListener('resize', this.updatePositionDebounced);
    
    // Listen for content update events
    if (this.$eventHub) {
      this.$eventHub.$on('tooltip-content-updated', this.handleContentUpdate);
    }
  },

  beforeDestroy() {
    // Remove scroll and resize listeners
    if (this.updatePositionDebounced) {
      window.removeEventListener('scroll', this.updatePositionDebounced, true);
      window.removeEventListener('resize', this.updatePositionDebounced);
    }
    
    // Remove content update listener
    if (this.$eventHub) {
      this.$eventHub.$off('tooltip-content-updated', this.handleContentUpdate);
    }
    
    // Clean up timeouts
    this.clearTimeouts();
    
    // Hide tooltip if visible
    if (this.isVisible) {
      this.hide();
    }
  }
};
</script>
